import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Grid, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, Resolver, useForm } from 'react-hook-form';

import { addAdultHolderDetailRequest } from '@/api/operation';

import { DatePicker } from '@/components/Form/DatePicker';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';

import { ERROR_CODE } from '@/constants/api';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs';
import { selectTerritories } from '@/redux/countries/selectors';

import { getAPIErrorCode, getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { getPhoneCode } from '@/utils/phoneNumber';

import {
  AddAdultDetailFormType,
  DEFAULT_ADULT_DETAIL_FORM_VALUES,
  getValidationSchema,
} from './helper';

type AddAdultDetailFormProps = {
  onSubmitted: (data: AddAdultDetailFormType) => void;
  onSubmitError: (error: unknown) => void;
};

export const AddAdultDetailForm = ({
  onSubmitted,
  onSubmitError,
}: AddAdultDetailFormProps) => {
  const { t } = useTranslate();
  const { phoneCodes } = useAppSelector(selectTerritories);
  const user = useAppSelector(selectLoggedInUser);
  const { dateFormat, jointAccountRelationshipOptions } =
    useAppSelector(selectConfigsState);

  const methods = useForm<AddAdultDetailFormType>({
    resolver: yupResolver(
      getValidationSchema(t),
    ) as Resolver<AddAdultDetailFormType>,
    mode: 'onChange',
    defaultValues: {
      ...DEFAULT_ADULT_DETAIL_FORM_VALUES,
      phoneCode: getPhoneCode(user?.tenant || ''),
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  const relationshipOptions = jointAccountRelationshipOptions.map(option => ({
    ...option,
    text: t(String(option.i18nKey)),
  }));

  const { mutate, isPending, error } = useMutation({
    mutationFn: addAdultHolderDetailRequest,
    onSuccess: () => {
      onSubmitted(methods.getValues());
    },
    onError: err => {
      onSubmitError(err);
    },
  });

  const onSubmit = (data: AddAdultDetailFormType) => {
    mutate({
      operationId: user?.operationId || '',
      payload: {
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        birthday: data.birthday,
        email: data.email,
        relationship: data.relationship,
        phoneNumber: `${data.phoneCode}${data.phoneNumber}`,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        direction="column"
        sx={{
          width: '100%',
        }}
        gap={4}
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextInput
          name="firstName"
          control={control}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
          disabled={isPending}
          fullWidth
          label={t('page.addAdultDetail.form.firstName.label')}
        />

        <TextInput
          name="middleName"
          control={control}
          error={!!errors.middleName}
          helperText={errors.middleName?.message}
          disabled={isPending}
          fullWidth
          label={t('page.addAdultDetail.form.middleName.label')}
        />

        <TextInput
          name="lastName"
          control={control}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
          disabled={isPending}
          fullWidth
          label={t('page.addAdultDetail.form.lastName.label')}
        />
        <DatePicker
          name="birthday"
          label={t('page.addAdultDetail.form.birthday.label')}
          format={dateFormat}
          control={control}
          error={!!errors.birthday}
          helperText={errors.birthday?.message}
          disableFuture
          disabled={isPending}
        />
        <TextInput
          type="email"
          inputMode="email"
          name="email"
          control={control}
          error={!!errors.email}
          helperText={errors.email?.message}
          disabled={isPending}
          fullWidth
          label={t('page.addAdultDetail.form.email.label')}
        />
        <Grid container columnSpacing={1.5} rowSpacing={2}>
          <Grid item xs={5}>
            <Select
              options={phoneCodes}
              control={control}
              name="phoneCode"
              label={t('page.addAdultDetail.form.phoneCode.label')}
              fullWidth
            />
          </Grid>
          <Grid item xs={7}>
            <TextInput
              type="tel"
              inputMode="tel"
              name="phoneNumber"
              control={control}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
              disabled={isPending}
              fullWidth
              inputProps={{
                maxLength: 7,
              }}
              label={t('page.addAdultDetail.form.phoneNumber.label')}
            />
          </Grid>
        </Grid>
        <Select
          options={relationshipOptions}
          control={control}
          name="relationship"
          error={!!errors.relationship}
          helperText={errors.relationship?.message}
          fullWidth
          disabled={isPending}
          placeholder={t('page.addAdultDetail.form.relationship.placeholder')}
          label={t('page.addAdultDetail.form.relationship.label')}
          useDrawerOnMobile
        />

        {shouldShowErrorAlert(error) && (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        )}

        <LoadingButton
          {...getDataTestId('add-adult-detail-submit')}
          type="submit"
          disabled={!isValid || isPending}
          isLoading={isPending}
          fullWidth
        >
          {t('common.button.continue')}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
};

function shouldShowErrorAlert(error: unknown) {
  const errorCode = getAPIErrorCode(error);
  return !!(error && errorCode !== ERROR_CODE.ADULT_UNDER_18);
}
